import type { SwitchboardPathTreeCount } from "@t3tools/contracts";

type Tone = "neutral" | "gate" | "exit" | "goal" | "unknown";
type PathNode = {
  id: string;
  label: string;
  x: number;
  y: number;
  tone: Tone;
};
type PathEdge = { source: string; target: string };

const COLORS: Record<Tone, string> = {
  neutral: "#8b929e",
  gate: "#e8a348",
  exit: "#e46a6a",
  goal: "#48b87a",
  unknown: "#a696d5",
};

const STAGES = [
  { x: 18, label: "DIAL" },
  { x: 160, label: "RING" },
  { x: 302, label: "GATEKEEPER" },
  { x: 444, label: "SCREENER / MENU RESULT" },
  { x: 586, label: "ON THE LINE" },
  { x: 728, label: "OUTCOME" },
  { x: 870, label: "HANDOFF" },
  { x: 1012, label: "ENCOUNTER" },
] as const;

// Keep each path stage aligned with the shared call path model. These fixed
// positions leave branches visible even when their filtered count is zero.
const NODES: readonly PathNode[] = [
  { id: "rang", label: "Rang", x: 18, y: 236, tone: "neutral" },
  { id: "provider_rejected", label: "Provider rejected", x: 18, y: 255, tone: "exit" },
  { id: "callback_unavailable", label: "Callback # unavailable", x: 18, y: 274, tone: "exit" },
  { id: "system_failed", label: "Our stack failed", x: 18, y: 293, tone: "exit" },
  { id: "failed_no_reason", label: "Failed, no reason", x: 18, y: 312, tone: "unknown" },

  { id: "answered", label: "Answered", x: 160, y: 165, tone: "neutral" },
  { id: "no_answer_busy", label: "No answer / busy", x: 160, y: 236, tone: "exit" },

  { id: "ai_screening", label: "AI screening", x: 302, y: 67, tone: "gate" },
  { id: "phone_menu", label: "IVR menu", x: 302, y: 141, tone: "gate" },
  { id: "silence_timeout", label: "Silence timeout", x: 302, y: 192, tone: "exit" },
  { id: "unknown_answerer", label: "Unknown answerer", x: 302, y: 262, tone: "unknown" },

  { id: "put_through", label: "Put through", x: 444, y: 39, tone: "gate" },
  { id: "sent_to_voicemail", label: "Sent to voicemail", x: 444, y: 58, tone: "gate" },
  { id: "screened_out", label: "Screened out", x: 444, y: 77, tone: "exit" },
  { id: "decision_unknown", label: "Decision unknown", x: 444, y: 96, tone: "unknown" },
  { id: "ivr_routed_on", label: "IVR routed on", x: 444, y: 128, tone: "gate" },
  { id: "stuck_in_menu", label: "Stuck in menu", x: 444, y: 147, tone: "exit" },

  { id: "person", label: "Person", x: 586, y: 243, tone: "neutral" },
  { id: "voicemail", label: "Voicemail", x: 586, y: 309, tone: "neutral" },

  { id: "not_available_now", label: "Not available now", x: 728, y: 189, tone: "neutral" },
  { id: "agent_dropped_mid_call", label: "Agent dropped mid-call", x: 728, y: 207, tone: "exit" },
  { id: "handoff_attempted", label: "Handoff attempted", x: 728, y: 289, tone: "neutral" },
  { id: "outcome_unknown", label: "Outcome unknown", x: 728, y: 308, tone: "unknown" },
  { id: "message_left", label: "Message left", x: 728, y: 328, tone: "goal" },
  { id: "message_not_recorded", label: "Message not recorded", x: 728, y: 347, tone: "exit" },

  { id: "connected_to_navigator", label: "Connected to navigator", x: 870, y: 270, tone: "goal" },
  { id: "navigator_never_joined", label: "No navigator available", x: 870, y: 289, tone: "exit" },
  { id: "navigator_left", label: "Navigator left", x: 870, y: 308, tone: "exit" },
  { id: "audio_not_ready", label: "Audio not ready", x: 870, y: 327, tone: "exit" },
  { id: "hung_up_while_holding", label: "Hung up while holding", x: 870, y: 346, tone: "exit" },
  {
    id: "handoff_failure_unknown",
    label: "Handoff reason unknown",
    x: 870,
    y: 365,
    tone: "unknown",
  },

  { id: "logged_encounter", label: "Logged encounter", x: 1012, y: 270, tone: "goal" },
  { id: "no_encounter", label: "No encounter", x: 1012, y: 289, tone: "exit" },
  { id: "encounter_unknown", label: "Encounter unknown", x: 1012, y: 308, tone: "unknown" },
];

const EDGES: readonly PathEdge[] = [
  { source: "rang", target: "answered" },
  { source: "rang", target: "no_answer_busy" },
  { source: "rang", target: "voicemail" },
  { source: "answered", target: "person" },
  { source: "answered", target: "unknown_answerer" },
  { source: "answered", target: "ai_screening" },
  { source: "answered", target: "phone_menu" },
  { source: "answered", target: "silence_timeout" },
  { source: "unknown_answerer", target: "person" },
  { source: "ai_screening", target: "put_through" },
  { source: "ai_screening", target: "sent_to_voicemail" },
  { source: "ai_screening", target: "screened_out" },
  { source: "ai_screening", target: "decision_unknown" },
  { source: "phone_menu", target: "ivr_routed_on" },
  { source: "phone_menu", target: "stuck_in_menu" },
  { source: "put_through", target: "person" },
  { source: "put_through", target: "voicemail" },
  { source: "sent_to_voicemail", target: "voicemail" },
  { source: "ivr_routed_on", target: "person" },
  { source: "ivr_routed_on", target: "voicemail" },
  { source: "voicemail", target: "message_left" },
  { source: "voicemail", target: "message_not_recorded" },
  { source: "person", target: "not_available_now" },
  { source: "person", target: "agent_dropped_mid_call" },
  { source: "person", target: "outcome_unknown" },
  { source: "person", target: "handoff_attempted" },
  { source: "person", target: "logged_encounter" },
  { source: "person", target: "no_encounter" },
  { source: "person", target: "encounter_unknown" },
  { source: "handoff_attempted", target: "connected_to_navigator" },
  { source: "handoff_attempted", target: "navigator_never_joined" },
  { source: "handoff_attempted", target: "navigator_left" },
  { source: "handoff_attempted", target: "audio_not_ready" },
  { source: "handoff_attempted", target: "hung_up_while_holding" },
  { source: "handoff_attempted", target: "handoff_failure_unknown" },
  { source: "connected_to_navigator", target: "logged_encounter" },
  { source: "connected_to_navigator", target: "no_encounter" },
];

const WIDTH = 1165;
const HEIGHT = 386;
const NODE_RADIUS = 4;

function edgePath(source: PathNode, target: PathNode) {
  const x1 = source.x + NODE_RADIUS;
  const x2 = target.x - NODE_RADIUS;
  const middle = (x1 + x2) / 2;
  return `M${x1},${source.y} C${middle},${source.y} ${middle},${target.y} ${x2},${target.y}`;
}

export function SwitchboardPathTree({
  counts,
  totalCalls,
}: {
  counts: readonly SwitchboardPathTreeCount[];
  totalCalls: number;
}) {
  const countByNode = new Map(counts.map((row) => [row.node, row.count]));
  const nodeById = new Map(NODES.map((node) => [node.id, node]));
  const description = [
    `Dial: ${totalCalls.toLocaleString()} filtered attempts`,
    ...NODES.map((node) => `${node.label}: ${(countByNode.get(node.id) ?? 0).toLocaleString()}`),
  ].join(". ");

  return (
    <figure>
      <figcaption className="mb-2 text-xs text-muted-foreground">
        {totalCalls.toLocaleString()} filtered attempts. Every modeled branch stays visible,
        including at 0; purple nodes mark unknown or unobserved steps.
      </figcaption>
      <div className="overflow-x-auto rounded-md border border-border/60 bg-[#17191f] px-3 py-3">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          className="min-w-[1165px] w-full"
          role="img"
          aria-label={`Switchboard call path tree for ${totalCalls.toLocaleString()} filtered attempts`}
        >
          <title>Switchboard call path tree</title>
          <desc>{description}</desc>
          {STAGES.map((stage) => (
            <text
              key={stage.label}
              x={stage.x}
              y={14}
              fill="#858c98"
              fontSize={8}
              fontWeight={600}
              letterSpacing="0.09em"
            >
              {stage.label}
            </text>
          ))}
          {EDGES.map((edge) => {
            const source = nodeById.get(edge.source)!;
            const target = nodeById.get(edge.target)!;
            return (
              <path
                key={`${edge.source}:${edge.target}`}
                d={edgePath(source, target)}
                fill="none"
                stroke="#66707d"
                strokeOpacity={0.48}
                strokeWidth={1}
              />
            );
          })}
          {NODES.map((node) => {
            const count = countByNode.get(node.id) ?? 0;
            return (
              <g key={node.id}>
                <circle cx={node.x} cy={node.y} r={NODE_RADIUS} fill={COLORS[node.tone]}>
                  <title>{`${node.label}: ${count.toLocaleString()} calls`}</title>
                </circle>
                <text x={node.x + 9} y={node.y + 3} fill="#e1e4e9" fontSize={10} fontWeight={500}>
                  {node.label}
                  <tspan fill="#aab0ba" fontWeight={400}>
                    {`  ${count.toLocaleString()}`}
                  </tspan>
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
        {(
          [
            ["neutral", "Call step"],
            ["gate", "Gatekeeper / route"],
            ["exit", "Call ends"],
            ["goal", "Goal reached"],
            ["unknown", "Unknown / unobserved"],
          ] as const
        ).map(([tone, label]) => (
          <span key={tone} className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full" style={{ backgroundColor: COLORS[tone] }} />
            {label}
          </span>
        ))}
        <span className="basis-full">
          “Navigator left” and “Handoff reason unknown” are included from the current call data.
        </span>
      </div>
    </figure>
  );
}
